/**
 * ProtectedRoute Component for Admin App
 *
 * Wrapper around shared ProtectedRoute that integrates with local AuthContext.
 * Используется для защиты маршрутов в Admin приложении.
 *
 * @example
 * import ProtectedRoute from '../components/ProtectedRoute';
 *
 * <ProtectedRoute requireAuth requireAdmin>
 *   <AdminDashboard />
 * </ProtectedRoute>
 */
import { Box, CircularProgress, Typography } from '@mui/material';
import SharedProtectedRoute from '@shared/components/ProtectedRoute/ProtectedRoute.js';
import { useAuth } from '../contexts/AuthContext';

/**
 * Admin-specific loading component
 */
const AdminLoadingComponent = () => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight="60vh"
    gap={2}
  >
    <CircularProgress />
    <Typography variant="body2" color="text.secondary">
      Проверка доступа...
    </Typography>
  </Box>
);

/**
 * Admin-specific ProtectedRoute wrapper
 *
 * Automatically passes AuthContext state to the shared component.
 * Default settings are optimized for admin panel (requireAdmin=true).
 */
const ProtectedRoute = ({
  children,
  requireAuth = true,
  requireB2B = false,
  requireAdmin = true, // Admin panel default
  loginPath = '/login',
  defaultPath = '/dashboard',
  ...otherProps
}) => {
  const { user, token, isAuthenticated, loading } = useAuth();

  // In admin, we persist auth via `token` and then resolve `user` via /api/auth/me.
  // During that gap, `isAuthenticated` can be false even though a token exists.
  // For route gating we must treat "token present" as authenticated.
  const hasToken = Boolean(token);

  // Build authState object for shared component
  const authState = {
    isAuthenticated: Boolean(isAuthenticated || hasToken),
    isLoading: loading,
    user,
    // Admin panel checks role directly
    // Admin routes are protected by token; role enforcement is ultimately server-side.
    // For routing we treat "token present" as sufficient for admin access.
    // If/when user is loaded we additionally enforce role.
    isAdmin: () => {
      if (!hasToken) return false;
      if (!user) return true;
      return user.role === 'admin' || user.role === 'super_admin';
    },
    hasB2BAccess: () => false, // Not applicable for admin
  };

  return (
    <SharedProtectedRoute
      authState={authState}
      requireAuth={requireAuth}
      requireB2B={requireB2B}
      requireAdmin={requireAdmin}
      loginPath={loginPath}
      defaultPath={defaultPath}
      LoadingComponent={AdminLoadingComponent}
      {...otherProps}
    >
      {children}
    </SharedProtectedRoute>
  );
};

export default ProtectedRoute;
